/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Контекст исполнения (this)</h2>
    <p className={styles.description}>
      Можно с уверенностью сказать, что ключевое слово <code className={styles.code}>this</code> является одной из самых
      запутанных частей JavaScript на старте изучения. Новички часто подставляют
      <code className={styles.code}>this</code> методом научного тыка до тех пор, пока скрипт не сработает 😐.
    </p>
    <p className={styles.description}>Контекст в JavaScript похож на контекст в предложении:</p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        <code className={styles.code}>Петя</code> бежит быстро, потому что <code className={styles.code}>Петя</code>{' '}
        пытается поймать поезд.
      </li>
      <li className={styles.list__item}>
        <code className={styles.code}>Петя</code> бежит быстро, потому что <code className={styles.code}>он</code>{' '}
        пытается поймать поезд.
      </li>
    </ul>
    <p className={styles.description}>
      Второе предложение звучит лаконичнее. Предметом предложения является Петя и мы можем сказать, что контекст
      предложения — это Петя, потому что он в центре внимания в это конкретное время в предложении. Даже местоимение{' '}
      <i>кто</i> относится к Пете.
    </p>
    <p className={styles.description}>И точно так же объект может быть текущим контекстом исполнения функции.</p>
    <CopyBlock
      text={`// Петя бежит быстро, потому что Петя пытается поймать поезд.
const petya = {
  name: 'Petya',
  showName() {
    console.log(petya.name);
  },
};

petya.showName();`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      Обращение к свойствам объекта внутри методов, используя имя самого объекта, аналогично использованию{' '}
      <code className={styles.code}>Петя</code> вместо <code className={styles.code}>он</code>.
    </p>
    <p className={styles.description}>
      У всех функций есть локальная переменная <code className={styles.code}>this</code>. Во время исполнения функции в
      переменную <code className={styles.code}>this</code> записывается ссылка на объект в контексте которого она
      вызывается.
    </p>
    <p className={styles.description}>
      <code className={styles.code}>this</code> нам нужен для доступа к методам и свойствам объекта, который вызывает
      функцию, тем более, что чаще всего имя вызывающего объекта не известно.
    </p>
    <CopyBlock
      text={`// Петя бежит быстро, потому что он (this) пытается поймать поезд.
const petya = {
  name: 'Petya',
  showName() {
    console.log(this.name);
  },
};

petya.showName();`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />

    <section className={styles.subsection}>
      <h2 className={styles.h2}>2 - Правила определения this</h2>
      <p className={styles.description}>
        Необходимо усвоить всего одно правило для определения <code className={styles.code}>this</code>.
      </p>
      <p className={styles.notificationStudy}>
        Значение контекста внутри функции определятся не в момент ее создания, а в момент вызова. То есть значение{' '}
        <code className={styles.codeError}>this</code> определяется тем, как вызывается функция, а не где она была
        объявлена.
      </p>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.1 - this в глобальной области видимости</h3>
        <p className={styles.description}>
          В глобальной области видимости, если скрипт выполняется не в строгом режиме,
          <code className={styles.code}>this</code> ссылается на объект <code className={styles.code}>window</code>. В
          строгом режиме значение <code className={styles.code}>this</code>, в глобальной области видимости, будет{' '}
          <code className={styles.code}>undefined</code>.
        </p>
        <CopyBlock
          text={`function fn() {
  console.log(this);
}

fn(); // window без "use strict" и undefined с "use strict"`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.2 - this в методе объекта</h3>
        <p className={styles.description}>
          Если функция была вызвана как метод объекта, то контекст будет ссылаться на объект, частью которого является
          метод.
        </p>
        <CopyBlock
          text={`const petya = {
  name: 'Petya',
  showThis() {
    console.log(this);
  },
  showName() {
    console.log(this.name);
  },
};

petya.showThis(); // {name: "Petya", showThis: ƒ, showName: ƒ}
petya.showName(); // 'Petya'`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>Более сложный пример для лучшего понимания:</p>
        <ul className={styles.list}>
          <li className={styles.list__item}>Сначала создадим функцию в глобальной области видимости и вызовем ее.</li>
          <li className={styles.list__item}>
            После чего присвоим ее в свойство объекта и вызовем как метод этого объекта.
          </li>
        </ul>
        <CopyBlock
          text={`function showThis() {
  console.log('this in showThis: ', this);
}

// Вызываем в глобальном контексте
showThis();
// this in showThis: Window

const user = { name: 'Mango' };

/*
 * Записываем ссылку на функцию в свойство объекта
 * Обратите внимание, что это не вызов - нет ()
 */
user.showContext = showThis;

/*
 * Вызываем функцию в контексте объекта
 * this будет указывать на текущий объект, в контексте
 * которого осуществляется вызов, а не на глобальный объект.
 */

user.showContext();
// this in showThis: {name: "Mango", showContext: ƒ}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.3 - this в функциях обратного вызова</h3>
        <p className={styles.description}>
          Когда мы передаем метод, использующий this в качестве параметра, который будет использоваться как функция
          обратного вызова, будет проблема. Решение этой проблемы рассматривается в следующей секции.
        </p>
        <CopyBlock
          text={`const hotel = {
  name: 'Resort Hotel',
  showThis() {
    console.log(this);
  },
};

const fn = function (callback) {
  /*
   * Во время вызова fn, callback будет ссылкой
   * на функцию showThis объекта hotel.
   * Ее вызов происходит в глобальном контексте,
   * про hotel она ничего не знает.
   * Соответственно this не будет ссылаться на hotel
   */
  callback();
};

// Передается ссылка на функцию а нее ее вызов
fn(hotel.showThis); // window или undefined`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>2.4 - this в стрелочных функциях</h3>
        <p className={styles.description}>
          Стрелочные функции не имеют своего <code className={styles.code}>this</code>. В отличии от обычных функций,
          изменить значение <code className={styles.code}>this</code> внутри стрелки после ее объявления нельзя.
        </p>
        <p className={styles.notificationStudy}>
          Контекст внутри стрелки определяется местом ее объявления, а не вызова и ссылается на контекст родительской
          функции.
        </p>
        <p className={styles.description}>
          Стрелочные функции также игнорируют наличие строгого режима. Если стрелка запомнила глобальный контекст, то{' '}
          <code className={styles.code}>this</code> в ней будет содержать ссылку на
          <code className={styles.code}>window</code> вне зависимости выполняется ли скрипт в строгом режиме или нет.
        </p>
        <CopyBlock
          text={`const showThis = () => {
  console.log('this in showThis: ', this);
};

showThis(); // this in showThis: window

const user = { name: 'Mango' };
user.showContext = showThis;

user.showContext(); // this in showThis: window`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Ограничивая стрелочные функции постоянным контекстом, JavaScript-движки могут лучше их оптимизировать, в
          отличие от обычных функций, значение <code className={styles.code}>this</code> которых может быть изменено.
        </p>
        <p className={styles.description}>
          Пример не практичный, но отлично показывает как работает контекст для стрелок. Значение контекста берется из
          родительской области видимости.
        </p>
        <CopyBlock
          text={`const hotel = {
  name: 'Resort hotel',
  showThis() {
    const fn = () => {
      /*
       * Стрелки запоминают контекст во время объявления,
       * из родительской области видимости
       */
      console.log('this in fn: ', this);
    };

    fn();
    console.log('this in showThis: ', this);
  },
};

hotel.showThis();
// this in fn: {name: 'Resort hotel', showThis: ƒ}
// this in showThis: {name: 'Resort hotel',showThis: ƒ}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>Если привести этот код к ES5, получится следующее.</p>
        <CopyBlock
          text={`const hotel = {
  name: 'Resort hotel',
  showThis: function showThis() {
    /*
     * Контекст для стрелки сохраняется
     * и передается из внешней области видимости
     */
    const context = this;

    const fn = function fn() {
      // А тут используется
      console.log('this in fn: ', context);
    };

    fn();
    console.log('this in showThis: ', this);
  },
};

hotel.showThis();
// this in fn: {name: 'Resort hotel', showThis: ƒ}
// this in showThis: {name: 'Resort hotel',showThis: ƒ}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>3 - Методы функций call, apply, bind</h2>
      <p className={styles.description}>
        Присвоение функции в качестве метода объекта может показаться хорошей идеей. Но стоит ли хранить подобные
        методы? Дублирование уже существующих функций в виде методов объекта будет занимать ресурсы, не принося никаких
        заметных выгод.
      </p>
      <p className={styles.description}>
        Теперь представьте что у вас 150 отелей и для каждого необходимо выполнить ту же самую операцию при каждом
        приветствии гостя. Нам, как минимум, хочется сразу вынести этот общий код в функцию.
      </p>
      <p className={styles.description}>
        Функция - это на самом деле довольно хитрый объект, поэтому у нее тоже есть методы. С помощью методов{' '}
        <code className={styles.code}>call</code> и <code className={styles.code}>apply</code> можно выполнить функцию в
        контексте какого-то объекта, не делая функцию его методом.
      </p>
      <CopyBlock
        text={`const greet = function () {
  return \`Wellcome to \${this.name} hotel!\`;
};

const hotel = { name: 'Resort Hotel' };

console.log(greet.call(hotel)); // "Wellcome to Resort Hotel!"
console.log(greet.apply(hotel)); // "Wellcome to Resort Hotel!"`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />

      <section className={styles.subsection}>
        <h3 className={styles.h3}>3.1 - call и аргументы</h3>
        <CopyBlock text={`fn.call(obj, arg1, arg2, ...)`} language="javascript" showLineNumbers={false} theme={ocean} />
        <p className={styles.description}>
          Запомнить правило использования <code className={styles.code}>call</code> довольно легко: метод{' '}
          <code className={styles.code}>call</code> вызовет функцию <code className={styles.code}>fn</code> передав ее{' '}
          <code className={styles.code}>this</code> ссылку на объект <code className={styles.code}>obj</code>, а также
          аргументы <code className={styles.code}>arg1</code>,<code className={styles.code}>arg2</code> и т. д.
        </p>
        <CopyBlock
          text={`const greet = function (guest, stars) {
  return \`\${guest}, welcome to \${stars}-star \${this.name}!\`;
};

const hotel = { name: 'Resort Hotel' };
const motel = { name: 'Sunlight Motel' };

console.log(greet.call(hotel, 'Mango', 5));
// "Mango, welcome to 5-star Resort Hotel!"

console.log(greet.call(motel, 'Poly', 4));
// "Poly, welcome to 4-star Sunlight Motel!"`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>3.2 - apply и аргументы</h3>
        <CopyBlock
          text={`fn.apply(obj, [arg1, arg2, ...])`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Метод <code className={styles.code}>apply</code> - полный аналог метода{' '}
          <code className={styles.code}>call</code> за исключением того, что синтаксис вызова аргументов требует не
          перечисление, а массив.
        </p>
        <CopyBlock
          text={`const greet = function (guest, stars) {
  return \`\${guest}, welcome to \${stars}-star \${this.name}!\`;
};

const hotel = { name: 'Resort Hotel' };
const motel = { name: 'Sunlight Motel' };

console.log(greet.apply(hotel, ['Mango', 5]));
// "Mango, welcome to 5-star Resort Hotel!"

console.log(greet.apply(motel, ['Poly', 4]));
// "Poly, welcome to 4-star Sunlight Motel!"
`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>3.3 - bind</h3>
        <p className={styles.description}>
          Мы рассмотрели случаи, когда необходимо мгновенно вызвать функцию с другим контекстом - для этого используются
          методы <code className={styles.code}>call</code> и <code className={styles.code}>apply</code>. Но в случае
          функции обратного вызова, когда необходимо не вызвать функцию на месте, а передать ссылку на эту функцию,
          причем с привязанным контекстом, <code className={styles.code}>call</code> и{' '}
          <code className={styles.code}>apply</code> не подходят. Метод <code className={styles.code}>bind</code>{' '}
          позволяет решить эту задачу.
        </p>
        <CopyBlock
          text={`const boundFn = fn.bind(obj, arg1, arg2, ...)`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Метод <code className={styles.code}>bind</code> создает копию функции <code className={styles.code}>fn</code>{' '}
          с привязанным контекстом <code className={styles.code}>obj</code> и аргументами{' '}
          <code className={styles.code}>arg1</code>, <code className={styles.code}>arg2</code> и так дале, после чего
          возвращает ее как результат своей работы. В результате мы получаем копию функции с привязанным контекстом,
          которую можно передать куда угодно и вызвать когда угодно.
        </p>
        <CopyBlock
          text={`const greet = function (guest) {
  return \`\${guest}, welcome to \${this.name}!\`;
};

const hotel = { name: 'Resort Hotel' };

const hotelGreeter = greet.bind(hotel, 'Mango');

hotelGreeter(); // "Mango, welcome to Resort Hotel!"`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Чаще всего метод <code className={styles.code}>bind</code> используется для привязки контекста при передаче
          методов объекта как функций обратного вызова. Возьмем проблемный пример из предыдущей секции. Задачу привязки
          контекста мы теперь можем решить используя метод <code className={styles.code}>bind</code>, передав функцией
          обратного вызова копию метода с привязанным контекстом.
        </p>
        <CopyBlock
          text={`const hotel = {
  name: 'Resort Hotel',
  showThis() {
    console.log(this);
  },
};

const fn = function (callback) {
  callback();
};

// Передаем копию метода showThis с контекстом привязанным к hotel
fn(hotel.showThis.bind(hotel)); // {name: "Resort Hotel", showThis: ƒ}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
  </section>
))
